






























































































































































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { GQL_timesheetWorkRelations_currentUnit_workRelations } from '@/queries/__generated__/timesheetWorkRelations';
import {
  GQL_workRelationTimesheet_workRelationTimesheet,
  GQL_workRelationTimesheet_workRelationTimesheet_days,
  GQL_workRelationTimesheet_workRelationTimesheet_days_breaks,
  GQL_workRelationTimesheet_workRelationTimesheet_days_timeStamps,
  GQL_workRelationTimesheet_workRelationTimesheet_timeStamps,
} from '@/queries/__generated__/workRelationTimesheet';
import { WORK_RELATION_TIMESHEET, SAVE_WORK_RELATION_TIMESHEET, SUBMIT_WORK_RELATION_TIMESHEET } from '@/queries/Timesheet';

// Times and durations are both kept as four digits ("0900", "0030") — the
// same shape the API uses — and only get their colon when displayed.
//
// Entries saved before breaks had start/end times only carry a total break
// duration; that comes through as a single break with `legacyMinutes` set and
// no times, kept until the user replaces it.
type BreakState = {
  startHhmm: string;
  endHhmm: string;
  legacyMinutes?: number;
};

// A named extra time column on the timesheet ("Overtime", "Night work"),
// added by the user. Its key is what the entries' spans are stored under.
type TimeStampDef = {
  key: string;
  name: string;
};

type TimeSpanState = {
  startHhmm: string;
  endHhmm: string;
};

type DayState = {
  startHhmm: string;
  endHhmm: string;
  breaks: BreakState[];
  // Keyed by TimeStampDef.key; every entry carries a span for every stamp
  // the timesheet has, so the template can bind to it directly.
  timeStamps: Record<string, TimeSpanState>;
};

// The subset of a saved timesheet we read back when populating the table.
// Both the query and the save mutation return this shape, so either response
// can be fed to applyTimesheet.
type TimesheetDayData = Pick<
  GQL_workRelationTimesheet_workRelationTimesheet_days,
  'date' | 'startHhmm' | 'endHhmm' | 'breakMinutes'
> & {
  breaks: Pick<GQL_workRelationTimesheet_workRelationTimesheet_days_breaks, 'startHhmm' | 'endHhmm'>[];
  timeStamps: Pick<GQL_workRelationTimesheet_workRelationTimesheet_days_timeStamps, 'key' | 'startHhmm' | 'endHhmm'>[];
};

type TimesheetData = {
  timeStamps: Pick<GQL_workRelationTimesheet_workRelationTimesheet_timeStamps, 'key' | 'name'>[];
  days: TimesheetDayData[];
};

// `key` gives Vue a stable identity for each row.
type BreakEntry = BreakState & {
  key: string;
};

// A single registered time span within a day. A day can hold more than one
// entry (e.g. 08:00-10:00 and 18:00-21:00), and each entry any number of
// breaks taken during it.
type DayEntry = Omit<DayState, 'breaks'> & {
  key: string;
  breaks: BreakEntry[];
};

type DayGroup = {
  date: string;
  dayOfMonth: number;
  weekdayLabel: string;
  entries: DayEntry[];
};

@Component
export default class TimesheetContent extends Vue {
  @Prop({ required: true }) private readonly workRelation!: GQL_timesheetWorkRelations_currentUnit_workRelations;
  @Prop({ required: true }) private readonly calendarMonth!: string;

  private loadingTimesheet = false;
  private actionInProgress = false;

  private isSubmitted = false;
  private groups: DayGroup[] = [];
  private timeStamps: TimeStampDef[] = [];

  private readonly maxEntriesPerDay = 3;
  private readonly maxBreaksPerEntry = 10;
  private readonly maxTimeStamps = 10;
  private entryKeySeq = 0;

  private get entries(): DayEntry[] {
    return this.groups.flatMap(group => group.entries);
  }

  // The stamp columns get a second header row for their start/end/total.
  private get headerRows() {
    return this.timeStamps.length ? 2 : 1;
  }

  get sumBreakHours() {
    return this.entries.reduce((sum, entry) => sum + this.breakHoursForEntry(entry), 0);
  }

  get sumPaidHours() {
    return this.entries.reduce((sum, entry) => sum + this.paidHoursForEntry(entry), 0);
  }

  private sumHoursForTimeStamp(stamp: TimeStampDef) {
    return this.entries.reduce((sum, entry) => sum + this.timeStampHours(entry.timeStamps[stamp.key]), 0);
  }

  private mounted() {
    this.loadTimesheet();
  }

  @Watch('workRelation.id')
  @Watch('calendarMonth')
  private async selectionChanged() {
    await this.loadTimesheet();
  }

  private emptyState(): DayState {
    return { startHhmm: '', endHhmm: '', breaks: [], timeStamps: {} };
  }

  private emptySpan(): TimeSpanState {
    return { startHhmm: '', endHhmm: '' };
  }

  private nextKey(prefix: string) {
    this.entryKeySeq += 1;
    return `${prefix}-${this.entryKeySeq}`;
  }

  private makeEntry(state?: DayState): DayEntry {
    const source = state || this.emptyState();
    // keys come last so fresh ones always win, even when copying an existing
    // entry (which already carries keys) into a new row. Breaks and stamp
    // spans are copied too, so the new row never shares them with its source.
    // Every stamp the timesheet has gets a span, even if the source had none.
    const timeStamps: Record<string, TimeSpanState> = {};
    this.timeStamps.forEach(stamp => {
      timeStamps[stamp.key] = { ...(source.timeStamps[stamp.key] || this.emptySpan()) };
    });

    return {
      ...source,
      breaks: source.breaks.map(brk => this.makeBreak(brk)),
      timeStamps,
      key: this.nextKey('entry'),
    };
  }

  private makeBreak(state?: BreakState): BreakEntry {
    return { ...(state || { startHhmm: '', endHhmm: '' }), key: this.nextKey('break') };
  }

  private stateFromDay(day: TimesheetDayData): DayState {
    const breaks: BreakState[] = day.breaks.map(brk => ({ startHhmm: brk.startHhmm, endHhmm: brk.endHhmm }));
    if (!breaks.length && day.breakMinutes > 0) {
      breaks.push({ startHhmm: '', endHhmm: '', legacyMinutes: day.breakMinutes });
    }

    const timeStamps: Record<string, TimeSpanState> = {};
    day.timeStamps.forEach(span => {
      timeStamps[span.key] = { startHhmm: span.startHhmm, endHhmm: span.endHhmm };
    });

    return {
      startHhmm: day.startHhmm || '',
      endHhmm: day.endHhmm || '',
      breaks,
      timeStamps,
    };
  }

  private buildEmptyGroups() {
    const start = this.$dayjs.strict(`${this.calendarMonth}-01`);
    const daysInMonth = start.daysInMonth();

    this.groups = Array.from({ length: daysInMonth }, (_, index) => {
      const day = index + 1;
      return {
        date: start.date(day).format('YYYY-MM-DD'),
        dayOfMonth: day,
        weekdayLabel: start.date(day).locale(this.$i18n.locale).format('dddd'),
        entries: [ this.makeEntry() ],
      };
    });
  }

  private groupByDate(date: string) {
    return this.groups.find(group => group.date === date) || null;
  }

  private addEntry(group: DayGroup) {
    if (this.isSubmitted) return;
    if (group.entries.length >= this.maxEntriesPerDay) return;
    group.entries.push(this.makeEntry());
  }

  private removeEntry(group: DayGroup, entryIndex: number) {
    if (this.isSubmitted) return;
    if (entryIndex <= 0) return; // the first row of the day always stays
    group.entries.splice(entryIndex, 1);
  }

  private addBreak(entry: DayEntry) {
    if (this.isSubmitted) return;
    if (entry.breaks.length >= this.maxBreaksPerEntry) return;
    entry.breaks.push(this.makeBreak());
  }

  private removeBreak(entry: DayEntry, breakIndex: number) {
    if (this.isSubmitted) return;
    entry.breaks.splice(breakIndex, 1);
  }

  private addTimeStamp() {
    if (this.isSubmitted) return;
    if (this.timeStamps.length >= this.maxTimeStamps) return;
    // Keys must stay unique across months too, since "copy from previous
    // month" carries stamps over with their keys.
    this.adoptTimeStamps([ { key: this.nextKey(`stamp-${Date.now()}`), name: '' } ]);
  }

  // Add stamp definitions the timesheet doesn't have yet (matched by key) and
  // give every entry a span for them, so the template can bind right away.
  private adoptTimeStamps(defs: TimeStampDef[]) {
    defs.forEach(def => {
      if (this.timeStamps.some(stamp => stamp.key === def.key)) return;
      if (this.timeStamps.length >= this.maxTimeStamps) return;

      this.timeStamps.push({ key: def.key, name: def.name });
      this.entries.forEach(entry => {
        if (!entry.timeStamps[def.key]) this.$set(entry.timeStamps, def.key, this.emptySpan());
      });
    });
  }

  private removeTimeStamp(stamp: TimeStampDef) {
    if (this.isSubmitted) return;

    const remove = () => {
      this.timeStamps = this.timeStamps.filter(other => other.key !== stamp.key);
      this.entries.forEach(entry => this.$delete(entry.timeStamps, stamp.key));
    };

    const hasTimes = this.entries.some(entry => {
      const span = entry.timeStamps[stamp.key];
      return span && (span.startHhmm || span.endHhmm);
    });
    if (!hasTimes) {
      remove();
      return;
    }

    this.$sPopup.confirm('', {
      title: this.$t('PopupTitle.confirmDelete'),
      message: this.$t('Timesheet.removeTimeStampConfirm', { name: stamp.name.trim() || '…' }),
      onConfirm: remove,
    });
  }

  // Rebuild the whole table from a saved timesheet. Days arrive sorted by
  // date, then chronologically by start time within a date, so this also
  // reflects any server-side reordering (e.g. after a save) without a reload.
  private applyTimesheet(timesheet: TimesheetData | null) {
    this.timeStamps = (timesheet?.timeStamps || []).map(stamp => ({ key: stamp.key, name: stamp.name }));
    this.buildEmptyGroups();

    const seenDates = new Set<string>();
    (timesheet?.days || []).forEach(day => {
      const group = this.groupByDate(day.date);
      if (!group) return;

      const entry = this.makeEntry(this.stateFromDay(day));

      if (seenDates.has(day.date)) {
        group.entries.push(entry);
      } else {
        group.entries = [ entry ];
        seenDates.add(day.date);
      }
    });
  }

  private async loadTimesheet() {
    this.buildEmptyGroups();
    this.loadingTimesheet = true;
    try {
      const res = await this.$apollo.query({
        query: WORK_RELATION_TIMESHEET,
        variables: {
          workRelationId: this.workRelation.id,
          calendarMonth: this.calendarMonth,
        },
        fetchPolicy: 'network-only',
      });

      this.isSubmitted = res.data.workRelationTimesheet?.status === 'submitted';
      this.applyTimesheet(res.data.workRelationTimesheet);
    } finally {
      this.loadingTimesheet = false;
    }
  }

  private isMonday(date: string) {
    return this.$dayjs.strict(date).day() === 1;
  }

  private async copyFromPreviousWeek(mondayDate: string) {
    if (this.isSubmitted) return;

    this.actionInProgress = true;
    try {
      const sourceStatesByDate: Record<string, DayState[]> = {};

      this.groups.forEach(group => {
        sourceStatesByDate[group.date] = group.entries.map(entry => ({ ...entry }));
      });

      const previousMonth = this.$dayjs.strict(mondayDate).subtract(7, 'day').format('YYYY-MM');
      if (previousMonth !== this.calendarMonth) {
        const res = await this.$apollo.query({
          query: WORK_RELATION_TIMESHEET,
          variables: {
            workRelationId: this.workRelation.id,
            calendarMonth: previousMonth,
          },
          fetchPolicy: 'network-only',
        });

        const previousTimesheet: GQL_workRelationTimesheet_workRelationTimesheet | null = res.data.workRelationTimesheet;
        this.adoptTimeStamps(previousTimesheet?.timeStamps || []);
        const previousStatesByDate: Record<string, DayState[]> = {};
        previousTimesheet?.days.forEach(day => {
          (previousStatesByDate[day.date] = previousStatesByDate[day.date] || []).push(this.stateFromDay(day));
        });
        Object.assign(sourceStatesByDate, previousStatesByDate);
      }

      for (let offset = 0; offset < 7; offset++) {
        const targetDate = this.$dayjs.strict(mondayDate).add(offset, 'day').format('YYYY-MM-DD');
        const sourceDate = this.$dayjs.strict(mondayDate).subtract(7, 'day').add(offset, 'day').format('YYYY-MM-DD');

        const sourceStates = sourceStatesByDate[sourceDate];
        const targetGroup = this.groupByDate(targetDate);
        if (!targetGroup) continue;

        targetGroup.entries = sourceStates.map(state => this.makeEntry(state));
      }
    } finally {
      this.$sMessage.success(this.$t('Timesheet.copyPreviousWeekSuccess'));
      this.actionInProgress = false;
    }
  }

  private async copyFromPreviousMonth() {
    if (this.isSubmitted) return;

    this.actionInProgress = true;
    try {
      const prevMonth = this.$dayjs.strict(`${this.calendarMonth}-01`).subtract(1, 'month').format('YYYY-MM');
      const res = await this.$apollo.query({
        query: WORK_RELATION_TIMESHEET,
        variables: {
          workRelationId: this.workRelation.id,
          calendarMonth: prevMonth,
        },
        fetchPolicy: 'network-only',
      });

      const prev: GQL_workRelationTimesheet_workRelationTimesheet | null = res.data.workRelationTimesheet;
      if (!prev) return;

      this.adoptTimeStamps(prev.timeStamps);
      const prevByKey: Record<string, DayState[]> = {};
      prev.days.forEach(day => {
        const key = this.weekdayOccurrenceKey(day.date);
        if (!key) return;
        (prevByKey[key] = prevByKey[key] || []).push(this.stateFromDay(day));
      });

      this.groups.forEach(group => {
        const key = this.weekdayOccurrenceKey(group.date);
        const templates = key ? prevByKey[key] : null;
        if (!templates || !templates.length) return;

        const shouldCopy = group.entries.every(entry => this.isEmptyState(entry));
        if (!shouldCopy) return;

        group.entries = templates.map(template => this.makeEntry(template));
      });
    } finally {
      this.$sMessage.success(this.$t('Timesheet.copyPreviousMonthSuccess'));
      this.actionInProgress = false;
    }
  }

  private weekdayOccurrenceKey(date: string): string | null {
    const d = this.$dayjs.strict(date);
    if (!d.isValid()) return null;

    const weekday = d.day();
    const start = d.startOf('month');
    let occurrence = 0;
    for (let day = 1; day <= d.date(); day++) {
      if (start.date(day).day() === weekday) occurrence += 1;
    }
    return `${weekday}-${occurrence}`;
  }

  private parseHhmm(value: string) {
    return (value || '').replace(/[^\d]/g, '').slice(0, 4);
  }

  private normalizeHhmmOnBlur(value: string) {
    const parsed = this.parseHhmm(value);
    if (parsed.length === 1) return `0${parsed}00`;
    if (parsed.length === 2) return `${parsed}00`;
    return parsed;
  }

  // "0900" -> "09:00", "090" -> "09:0" (partial input keeps its colon so the
  // field doesn't jump around while typing).
  private formatHhmm(hhmm: string) {
    const digits = this.parseHhmm(hhmm);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  }

  private isEmptyState(state: DayState) {
    return !state.startHhmm && !state.endHhmm &&
      state.breaks.every(brk => !brk.startHhmm && !brk.endHhmm && brk.legacyMinutes == null) &&
      Object.values(state.timeStamps).every(span => !span.startHhmm && !span.endHhmm);
  }

  private totalHoursForEntry(entry: DayEntry) {
    return this.calculateTotalHours(entry.startHhmm, entry.endHhmm);
  }

  private timeStampHours(span: TimeSpanState | undefined) {
    if (!span) return 0;
    return this.calculateTotalHours(span.startHhmm, span.endHhmm);
  }

  // A stamp span is independent of the working period (an "Overtime"
  // 16:00-22:00 next to a 09:00-14:00 day), so it only has to be complete.
  private timeStampError(span: TimeSpanState | undefined): string | null {
    if (!span) return null;
    if (!!span.startHhmm === !!span.endHhmm) return null;
    return this.$t('Timesheet.timeStampIncomplete') as string;
  }

  private breakHoursForBreak(brk: BreakEntry) {
    if (brk.legacyMinutes != null) return brk.legacyMinutes / 60;
    return this.calculateTotalHours(brk.startHhmm, brk.endHhmm);
  }

  private breakHoursForEntry(entry: DayEntry) {
    return entry.breaks.reduce((sum, brk) => sum + this.breakHoursForBreak(brk), 0);
  }

  private paidHoursForEntry(entry: DayEntry) {
    return Math.max(0, this.totalHoursForEntry(entry) - this.breakHoursForEntry(entry));
  }

  private calculateTotalHours(startHhmm: string, endHhmm: string) {
    const start = this.hhmmToMinutes(startHhmm);
    const end = this.hhmmToMinutes(endHhmm);
    if (start == null || end == null) return 0;

    let diff = end - start;
    if (diff < 0) diff += 24 * 60; // allow overnight shifts
    return diff / 60;
  }

  // A time earlier on the clock than the one it follows belongs to the next
  // calendar day (an overnight shift's end, a break after midnight).
  private isNextDay(baseHhmm: string, hhmm: string) {
    const base = this.hhmmToMinutes(baseHhmm);
    const minutes = this.hhmmToMinutes(hhmm);
    return base != null && minutes != null && minutes < base;
  }

  private hhmmToMinutes(hhmm: string): number | null {
    const v = (hhmm || '').trim();
    if (!v.match(/^\d{4}$/)) return null;
    const hours = Number(v.slice(0, 2));
    const minutes = Number(v.slice(2, 4));
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    // Valid up to 24:00 (end of day), with the minutes part never above 59.
    if (Number(v) > 2400 || minutes > 59) return null;
    return (hours * 60) + minutes;
  }

  // A time or duration is invalid once four digits are entered but they don't
  // form a real time (bigger than 2400, or the minutes part above 59). Fewer
  // than four digits is treated as "still typing", not invalid.
  private isInvalidHhmm(value: string) {
    if (!value) return false;
    if (value.length < 4) return true;
    return this.hhmmToMinutes(value) == null;
  }

  // Why a break can't be saved as it stands, as a message for the user, or
  // null when it's fine. Mirrors the checks the save mutation does: a break
  // needs both times, lies inside the entry's working period, and doesn't
  // overlap the entry's other breaks. Times are measured from the entry's
  // start so an overnight shift (21:00-07:00) can hold a break at 02:00-03:00.
  private breakError(entry: DayEntry, brk: BreakEntry): string | null {
    if (brk.legacyMinutes != null) return null;
    if (!brk.startHhmm && !brk.endHhmm) return null;

    const breakStart = this.hhmmToMinutes(brk.startHhmm);
    const breakEnd = this.hhmmToMinutes(brk.endHhmm);
    if (breakStart == null || breakEnd == null) {
      // Bad digits get their own red already; only an outright missing side
      // needs saying.
      if (!brk.startHhmm || !brk.endHhmm) return this.$t('Timesheet.breakIncomplete') as string;
      return null;
    }

    const workStart = this.hhmmToMinutes(entry.startHhmm);
    const workEnd = this.hhmmToMinutes(entry.endHhmm);
    if (workStart == null || workEnd == null) return this.$t('Timesheet.breakWithoutWorkingHours') as string;

    const since = (minutes: number) => (minutes < workStart ? minutes + (24 * 60) : minutes);
    const start = since(breakStart);
    const end = since(breakEnd);
    if (!(start < end && end <= since(workEnd))) {
      return this.$t('Timesheet.breakOutsideWorkingHours', {
        start: this.formatHhmm(entry.startHhmm),
        end: this.formatHhmm(entry.endHhmm),
      }) as string;
    }

    const overlapsOther = entry.breaks.some(other => {
      if (other === brk) return false;
      const otherStart = this.hhmmToMinutes(other.startHhmm);
      const otherEnd = this.hhmmToMinutes(other.endHhmm);
      if (otherStart == null || otherEnd == null) return false;
      return start < since(otherEnd) && since(otherStart) < end;
    });
    if (overlapsOther) return this.$t('Timesheet.breaksOverlap') as string;

    return null;
  }

  private get hasInvalidTimes(): boolean {
    if (this.timeStamps.some(stamp => !stamp.name.trim())) return true;

    return this.groups.some(group =>
      group.entries.some(entry =>
        this.isInvalidHhmm(entry.startHhmm) || this.isInvalidHhmm(entry.endHhmm) ||
        entry.breaks.some(brk =>
          this.isInvalidHhmm(brk.startHhmm) || this.isInvalidHhmm(brk.endHhmm) || !!this.breakError(entry, brk),
        ) ||
        Object.values(entry.timeStamps).some(span =>
          this.isInvalidHhmm(span.startHhmm) || this.isInvalidHhmm(span.endHhmm) || !!this.timeStampError(span),
        ),
      ),
    );
  }

  // Read-only cells and sums: 1.5 -> "01:30". Monthly sums can pass 99 hours,
  // so the hours part simply grows ("162:30").
  private formatDuration(hours: number) {
    if (!Number.isFinite(hours)) return '';
    return this.minutesToHhmm(this.hoursToMinutes(hours), ':');
  }

  private minutesToHhmm(minutes: number, separator = '') {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${`${hours}`.padStart(2, '0')}${separator}${`${mins}`.padStart(2, '0')}`;
  }

  private hoursToMinutes(hours: number) {
    return Math.round(hours * 60);
  }

  private async saveTimesheet() {
    if (this.isSubmitted) return;

    this.actionInProgress = true;
    try {
      // Flatten day groups into a flat list of entries. Groups are already in
      // date order and entries keep their registration order within a day, so
      // the server stores and re-serves them in the same order.
      const days = this.groups.flatMap(group =>
        group.entries.map(entry => {
          const breakMinutes = this.hoursToMinutes(this.breakHoursForEntry(entry));
          const paidMinutes = this.hoursToMinutes(this.paidHoursForEntry(entry));
          return {
            date: group.date,
            startHhmm: entry.startHhmm || null,
            endHhmm: entry.endHhmm || null,
            breakMinutes,
            breaks: entry.breaks
              .filter(brk => brk.legacyMinutes == null && (brk.startHhmm || brk.endHhmm))
              .map(brk => ({ startHhmm: brk.startHhmm, endHhmm: brk.endHhmm })),
            timeStamps: this.timeStamps
              .map(stamp => ({ key: stamp.key, ...entry.timeStamps[stamp.key] }))
              .filter(span => span.startHhmm || span.endHhmm),
            totalMinutes: this.hoursToMinutes(this.totalHoursForEntry(entry)),
            paidMinutes,
          };
        }),
      );

      const timeStamps = this.timeStamps.map(stamp => ({
        key: stamp.key,
        name: stamp.name.trim(),
        totalMinutes: this.hoursToMinutes(this.sumHoursForTimeStamp(stamp)),
      }));

      const res = await this.$apollo.mutate({
        mutation: SAVE_WORK_RELATION_TIMESHEET,
        variables: {
          timesheet: {
            workRelationId: this.workRelation.id,
            calendarMonth: this.calendarMonth,
            timeStamps,
            days,
            totalMinutesSum: days.reduce((sum, day) => sum + day.totalMinutes, 0),
            paidMinutesSum: days.reduce((sum, day) => sum + day.paidMinutes, 0),
          },
        },
      });

      // Re-render from the saved result so entries reflect the server's
      // chronological ordering immediately, without needing a reload.
      const timesheet = res.data?.saveWorkRelationTimesheet?.timesheet;
      if (timesheet) {
        this.isSubmitted = timesheet.status === 'submitted';
        this.applyTimesheet(timesheet);
      }

      this.$sMessage.success(this.$t('Timesheet.savingTimesheetSuccess'));
      this.$emit('saved');
    } finally {
      this.actionInProgress = false;
    }
  }

  private async submitTimesheet() {
    if (this.isSubmitted) return;

    this.actionInProgress = true;
    try {
      await this.saveTimesheet();
      const res = await this.$apollo.mutate({
        mutation: SUBMIT_WORK_RELATION_TIMESHEET,
        variables: {
          workRelationId: this.workRelation.id,
          calendarMonth: this.calendarMonth,
        },
      });
      this.$sMessage.success(this.$t('Timesheet.lockingTimesheetSuccess'));
      this.isSubmitted = res.data.submitWorkRelationTimesheet.timesheet.status === 'submitted';
    } finally {
      this.actionInProgress = false;
    }
  }
}
